import React, { createContext, useContext } from 'react';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { usePublishEntitlement } from '@/src/modules/entitlements/usePublishEntitlement';

export interface CatalogPricingMode {
  /** True if the user has a partner role or has permission to purchase partner credit packs. */
  isPartnerUser: boolean;
  /** True if the current user has available funds (wallet balance or remaining credits). */
  hasCredits: boolean;
  /** The number of remaining publish credits available. */
  remainingCredits: number;
  /** The wallet balance in rupees (for partner wallet users). */
  walletBalance?: number;
  /**
   * Formatted balance string suitable for display:
   * e.g. "₹500" if walletBalance > 0, otherwise "3 credits" / "1 credit", or "₹0" if empty.
   */
  balanceFormatted?: string;
  /**
   * True if retail prices should be suppressed and replaced with credit indicators.
   * STRICTLY ACTIVE FOR PARTNERS ONLY. Retail users keep retail prices and checkout paths
   * because credits are template-bound and non-partner checkouts must not be blocked.
   */
  isCreditMode: boolean;
  /** Entitlement loading state. */
  loading: boolean;
}

const CatalogPricingContext = createContext<CatalogPricingMode | null>(null);

function useInternalPricingMode(enabled: boolean): CatalogPricingMode {
  const isPartner = useAuthStore((s) => s.isPartner());
  const permissions = useAuthStore((s) => s.permissions ?? []);
  const isAuthenticated = useAuthStore((s) => {
    if (typeof s.isAuthenticated === 'function') return s.isAuthenticated();
    if (s.user !== undefined) return Boolean(s.user);
    return true;
  });
  const canBuyCreditPacks = permissions.includes('PURCHASE_CREDIT_PACK');
  const isPartnerUser = isPartner || canBuyCreditPacks;

  // PQ-48: Skip /v1/me/entitlements query when the user is not signed in, avoiding 401s in public catalog
  const shouldFetch = enabled && Boolean(isAuthenticated);
  const { data: entitlement, loading } = usePublishEntitlement({ enabled: shouldFetch });
  const walletBalance = entitlement?.walletBalance ?? 0;
  const remainingCredits = entitlement?.remainingCredits ?? 0;
  const hasCredits = isPartnerUser
    ? (walletBalance > 0 || remainingCredits > 0)
    : remainingCredits > 0;

  // Show ₹ strictly for wallet balance; credit count in credit wording
  const balanceFormatted =
    walletBalance > 0
      ? `₹${walletBalance.toLocaleString('en-IN')}`
      : remainingCredits > 0
        ? `${remainingCredits} ${remainingCredits === 1 ? 'credit' : 'credits'}`
        : '₹0';

  // Credit mode is strictly for partners. Non-partners keep retail checkout.
  const isCreditMode = isPartnerUser;

  return {
    isPartnerUser,
    hasCredits,
    remainingCredits,
    walletBalance,
    balanceFormatted,
    isCreditMode,
    loading,
  };
}

/**
 * Provider that hoists entitlement loading to the catalog container level.
 * Prevents N cards in a grid from each firing individual /v1/me/entitlements queries.
 */
export function CatalogPricingProvider({ children }: { children: React.ReactNode }) {
  const mode = useInternalPricingMode(true);
  return React.createElement(CatalogPricingContext.Provider, { value: mode }, children);
}

/**
 * useCatalogPricingMode
 *
 * Reads from CatalogPricingContext if rendered within CatalogPricingProvider,
 * or computes directly if rendered standalone (e.g. in unit tests).
 */
export function useCatalogPricingMode(): CatalogPricingMode {
  const context = useContext(CatalogPricingContext);
  // Only fetch when there is no provider above us; inside one, the provider already did.
  const fallback = useInternalPricingMode(!context);
  return context ?? fallback;
}
